<template>
  <div class="file-uploader-control">
    <b-overlay :show="busy">
      <template #overlay>
        <div class="text-center">
          <i class="pull-right fa fa-spinner fa-spin"/>
          <p id="cancel-label">Uploading...</p>
        </div>
      </template>

      <div :aria-hidden="busy ? 'true' : null">
        <!-- Uploaded files list -->
        <div v-if="value && value.length > 0">
          <h3 class="mt-3">File Attachments:</h3>
          <ul class="uploaded-file-list">
            <li v-for="(f, index) in value" :key="'uploaded-' + index">
              <img
                :src="f.url"
                width="25"
                height="25"
                @error="onThumbError"
              >
              <a :href="f.url" target="_blank"> {{ f.name }}</a>
              <i
                v-if="!removeLoading[index]"
                :id="`remove-file-${index}`"
                class="fa fa-trash"
                style="cursor: pointer;"
                @click="removeFile(f, index)"
              />
              <i
                v-if="removeLoading[index]"
                :id="`spin-file-${index}`"
                class="fa fa-spinner fa-spin"
              />
            </li>
          </ul>
        </div>
        <div v-else>
          <span>No Attachments available</span>
        </div>

        <!-- Drop zone -->
        <div
          class="drop-zone"
          :class="{ 'drop-zone--active': isDragOver }"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
        >
          <div v-if="isDragOver" class="drop-zone__overlay">
            <h4>Drop files here</h4>
          </div>
          <div class="text-center p-4">
            <label :for="uniqueInputId" class="btn btn-lg btn-primary mb-2">Select Files</label>
            <p class="mb-0 text-muted">or drag and drop</p>
            <input
              :id="uniqueInputId"
              ref="fileInput"
              type="file"
              multiple
              :accept="acceptedTypes"
              style="display: none;"
              @change="onFilesSelected"
            >
          </div>
        </div>

        <!-- Upload queue (currently uploading) -->
        <div v-if="uploadQueue.length > 0" class="mt-2">
          <div v-for="(qf, qi) in uploadQueue" :key="'queue-' + qi" class="d-flex align-items-center mb-1">
            <i class="fa fa-spinner fa-spin mr-2"/>
            <span class="text-muted">{{ qf.name }}</span>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { CONTROL_FIELD_EXTEND_MIXIN } from '../../mixins/control-field-extend-mixin';
import { mapActions } from 'vuex';
import LogRocket from 'logrocket';
import { validateFile, uploadToS3, getAcceptedFileTypes } from '@/utils/fileUpload';
import { sanitizeOnPremAttachmentName } from '@/utils/sanitizeOnPremFilename';

export default {
  name: 'FileUploaderControl',
  mixins: [CONTROL_FIELD_EXTEND_MIXIN],

  data: () => ({
    busy: false,
    isDragOver: false,
    dragCounter: 0,
    uploadedNames: [],
    uploadQueue: [],
    removeLoading: [],
  }),

  computed: {
    uniqueInputId() {
      return `file-${this.control.uniqueId || this.control.name || 'upload'}`;
    },

    acceptedTypes() {
      return getAcceptedFileTypes();
    },
  },

  mounted() {
    // Initialize uploadedNames from any existing value
    if (this.value && this.value.length > 0) {
      this.uploadedNames = this.value.map(f => f.name);
    }
  },

  methods: {
    ...mapActions('forms', ['removeFormAttachment']),

    /**
     * Handle thumbnail load errors (PDF, doc files won't have image previews)
     */
    onThumbError(e) {
      e.target.style.display = 'none';
    },

    // -- Drag and drop handlers using dragCounter pattern --

    onDragEnter() {
      this.dragCounter++;
      this.isDragOver = true;
    },

    onDragLeave() {
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isDragOver = false;
      }
    },

    onDrop(e) {
      this.dragCounter = 0;
      this.isDragOver = false;
      const files = e.dataTransfer && e.dataTransfer.files;
      if (files && files.length > 0) {
        this.processFiles(files);
      }
    },

    // -- File input handler --

    onFilesSelected(e) {
      const files = e.target.files;
      if (files && files.length > 0) {
        this.processFiles(files);
      }
      // Reset input so the same file can be re-selected
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // -- Core upload logic --

    async processFiles(fileList) {
      const files = Array.from(fileList);

      for (const file of files) {
        // Validate
        const validation = validateFile(file);
        if (!validation.valid) {
          LogRocket.warn(`File validation failed: ${validation.error}`);
          this.$toast.error(validation.error);
          continue;
        }

        // Selfie rename: if filename is "image.jpg", make it unique
        let fileName = file.name;
        if (fileName === 'image.jpg') {
          fileName = `image_${Date.now()}.jpg`;
        }

        // The on-prem EXE writes this name into the OpenDental imaging folder AND (on builds
        // before teamio-exe 1b293e8, which is most of the fleet) interpolates it into a SQL
        // literal, so an apostrophe ("Driver's License.jpg") is a parse error and a silently lost
        // attachment. forms-service sanitizes again on the send path — that is the load-bearing
        // guard. This call is here so the name shown in the list, and stored in the submission,
        // is the same name modulo what the backend adds.
        //
        // NOTE (2026-08-23): what reaches OpenDental is no longer exactly this string. The send
        // path appends a per-document content token — "Insurance Card_a41f9c2e.pdf" — because the
        // EXE treats (PatNum, FileName) as document identity, so two different documents under one
        // filename used to collapse into one silently. Wire-only; nothing to do here. See
        // forms-service/lib/attachmentFileNaming.js.
        fileName = sanitizeOnPremAttachmentName(fileName, 'Patient Attachment');

        // Check duplicate
        if (this.uploadedNames.includes(fileName)) {
          this.$toast.error('You have already uploaded a file with this name');
          continue;
        }

        // Add to upload queue for visual feedback
        const queueEntry = { name: fileName };
        this.uploadQueue.push(queueEntry);
        this.busy = true;

        try {
          LogRocket.log(`in FileUploaderControl uploading: ${fileName}`);

          const result = await uploadToS3({
            file: new File([file], fileName, { type: file.type }),
            siteID: this.$attrs.siteID,
            teamioType: 'FORM',
            patNum: this.$attrs.patNum,
          });

          // Update the single source of truth
          this.updateValue([
            ...(this.value || []),
            { name: fileName, url: result.url },
          ]);

          this.uploadedNames.push(fileName);

          // Remove from upload queue after a brief delay
          setTimeout(() => {
            const idx = this.uploadQueue.indexOf(queueEntry);
            if (idx !== -1) {
              this.uploadQueue.splice(idx, 1);
            }
          }, 2000);
        } catch (err) {
          console.error('Upload failed:', err);
          LogRocket.error('FileUploaderControl upload failed', err);
          this.$toast.error('File upload failed. Please try again.');

          // Remove from queue immediately on error
          const idx = this.uploadQueue.indexOf(queueEntry);
          if (idx !== -1) {
            this.uploadQueue.splice(idx, 1);
          }
        }
      }

      this.busy = false;
    },

    // -- Delete --

    async removeFile(f, index) {
      this.$set(this.removeLoading, index, true);

      try {
        const payload = {
          ...f,
          siteID: this.$attrs.siteID,
          formSubmissionIDForWeb: this.$attrs.formSubmissionIDForWeb,
        };
        await this.removeFormAttachment(payload);

        const updated = this.value.filter(v => !(v.name === f.name && v.url === f.url));
        this.updateValue(updated);

        // Remove from uploadedNames so user can re-upload same filename
        const nameIdx = this.uploadedNames.indexOf(f.name);
        if (nameIdx !== -1) {
          this.uploadedNames.splice(nameIdx, 1);
        }

        this.$set(this.removeLoading, index, false);
      } catch (err) {
        console.error('Remove failed:', err);
        LogRocket.error('FileUploaderControl remove failed', err);
        this.$toast.error('Failed to remove attachment. Please try again.');
        this.$set(this.removeLoading, index, false);
      }
    },
  },
};
</script>

<style scoped>
.file-uploader-control .uploaded-file-list {
  list-style: none;
  padding-left: 0;
}

.file-uploader-control .uploaded-file-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.drop-zone {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 8px;
  transition: border-color 0.2s, background-color 0.2s;
  margin-top: 12px;
}

.drop-zone--active {
  border-color: #007bff;
  background-color: #e8f0fe;
}

/* ControlView.vue binds `is-invalid` (STYLES.FORM.ERROR_OUTLINE) on this component's root when the
   control fails validation. Bootstrap only paints .form-control inputs red, so surface the failure
   on the drop zone the patient is looking at. #dc3545 = Bootstrap danger. */
.file-uploader-control.is-invalid .drop-zone {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.drop-zone__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 123, 255, 0.08);
  border-radius: 8px;
  z-index: 1;
  pointer-events: none;
}

.drop-zone__overlay h4 {
  color: #007bff;
  margin: 0;
}
</style>
